import type { ReactElement, ReactNode } from 'react';

import { SiteHeader } from '@/components/layout/SiteHeader.js';
import { cn } from '@/lib/utils.js';

/**
 * Оболочка экранов-исходов: ink-шапка, круглый значок исхода, заголовок, текст, действие.
 * Артборд 5d. Ровно эта форма в проде повторена четырежды — verified_page.php, pay_ok.php,
 * pay_fail.php и error.php; там она скопирована в каждый шаблон, здесь это один компонент.
 *
 * ⚠️ **Сигнал исхода переехал с цвета КНОПКИ на ИКОНКУ.** В проде исход читался по кнопке
 * возврата: зелёная на подтверждении почты и успешной оплате, красная на неудачной, чёрная
 * на 404 — человек понимал результат раньше, чем читал текст. В макете кнопка везде тёмная,
 * а смысл несёт круг сверху: зелёная галочка на светло-зелёном против красного
 * восклицательного знака в красной окружности.
 *
 * Это осознанная замена, а не потеря: сигнал стал крупнее и переехал наверх, к заголовку.
 * Но он ОБЯЗАН оставаться — унификация значка «чтобы везде одинаково» сотрёт разницу между
 * «деньги списаны» и «операция не прошла».
 *
 * ⚠️ Цвет и здесь не единственный носитель: у значка есть `aria-label` с текстом исхода,
 * потому что «✓» и «!» — символы, которые скринридер прочитает как пунктуацию.
 */
type Outcome = 'success' | 'failure' | 'neutral';

const MARK: Record<Outcome, { className: string; glyph: string }> = {
  success: { className: 'bg-mm-ok-bg text-mm-success-text', glyph: '✓' },
  failure: { className: 'border-2 border-mm-danger text-mm-danger', glyph: '!' },
  neutral: { className: 'bg-mm-surface text-mm-ink', glyph: '·' },
};

export function OutcomePage({
  outcome,
  markLabel,
  children,
  className,
}: {
  /** `neutral` — исходы без окраски: 404 и промежуточные состояния оплаты. */
  outcome?: Outcome;
  /** Текстовое имя исхода для скринридера. Обязательно, когда значок показан. */
  markLabel?: string;
  children: ReactNode;
  className?: string;
}): ReactElement {
  const mark = outcome !== undefined ? MARK[outcome] : undefined;

  return (
    <div className="flex min-h-dvh flex-col bg-white text-mm-ink">
      <SiteHeader />

      <main
        id="main"
        className={cn(
          'flex flex-1 flex-col items-center justify-center gap-[18px] px-5 py-16 text-center md:px-11',
          className,
        )}
      >
        {mark !== undefined && (
          <span
            role="img"
            aria-label={markLabel}
            className={cn(
              'flex size-14 items-center justify-center rounded-full text-[26px] font-bold',
              mark.className,
            )}
          >
            {mark.glyph}
          </span>
        )}
        {children}
      </main>
    </div>
  );
}
